import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Heart, Search, MapPin, Mail, Phone, Clock, Star, Users, Shield, HelpCircle, FileText, Cookie, Info, ExternalLink, BookOpen, QrCode, ToggleRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

/* ─────────────────────────────────────────────
   REUSABLE COMPONENTS
───────────────────────────────────────────── */

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-amber-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={20} className="text-amber-600" />
          <span className="font-semibold text-gray-800 text-base">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={20} className="text-amber-500" /> : <ChevronDown size={20} className="text-gray-400" />}
      </button>
      {isOpen && <div className="p-6 bg-white">{children}</div>}
    </div>
  );
};

const NavLink = ({ href, children, icon: Icon, activeSection, setActiveSection }) => {
  const handleClick = (e) => {
    e.preventDefault();
    setActiveSection(children);
    const el = document.getElementById(href.replace('#', ''));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <a
      href={href}
      onClick={handleClick}
      className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg transition-colors text-sm font-medium
        ${activeSection === children ? 'text-amber-600 bg-amber-50 underline' : 'text-gray-600 hover:text-amber-600 hover:bg-amber-50'}`}
    >
      <Icon size={15} className="hidden sm:inline flex-shrink-0" />
      <span>{children}</span>
    </a>
  );
};

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left">
        <span className="font-medium text-gray-800 pr-4">{question}</span>
        {open ? <ChevronUp size={18} className="text-amber-600 flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-4 text-gray-600 leading-relaxed text-sm">{answer}</p>}
    </div>
  );
};

/* Legal document sub-components */
const LegalH2 = ({ children }) => (
  <h3 className="text-base font-bold text-gray-900 mt-8 mb-3 pb-1 border-b border-amber-100">{children}</h3>
);
const LegalH3 = ({ children }) => (
  <h4 className="text-sm font-semibold text-gray-800 mt-5 mb-2">{children}</h4>
);
const LegalP = ({ children }) => (
  <p className="text-gray-600 text-sm leading-relaxed mb-3">{children}</p>
);
const LegalUL = ({ items }) => (
  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm mb-3 ml-2">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);
const LegalNote = ({ children }) => (
  <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-3 my-4 text-sm text-amber-800">{children}</div>
);

/* ─────────────────────────────────────────────
   PRIVACY POLICY CONTENT
───────────────────────────────────────────── */
const PrivacyPolicyContent = () => (
  <div>
    <p className="text-xs text-gray-400 mb-1">Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: January 2025</p>
    <LegalNote>
      This Privacy Policy applies to all users of the Gemio mobile and web application. Please read it carefully before using our services.
    </LegalNote>

    {/* SECTION 1 */}
    <LegalH2>1. Introduction</LegalH2>
    <LegalH3>1.1 Who We Are</LegalH3>
    <LegalP>
      Gemio ("Gemio", "we", "us", or "our") is a digital restaurant discovery, menu exploration, and reservation management platform operating exclusively within the Republic of Kenya. Gemio is currently operated by an individual operator at the pre-incorporation stage, with the intention of formalising as a registered legal entity under the Laws of Kenya. Our registered contact for data and legal purposes is reachable at <strong>business.hiddengems@gmail.com</strong>.
    </LegalP>

    <LegalH3>1.2 Nature of Services</LegalH3>
    <LegalP>
      Gemio provides users with a curated digital experience that includes: (i) discovery and exploration of restaurants and dining establishments across Kenya; (ii) browsing of restaurant menus through digitised menu links and QR code technology; (iii) online table reservation management for participating restaurants; and (iv) a restaurant dashboard enabling business owners to manage their public profiles, menu content, and reservation availability. Gemio does not operate any food establishment, process financial payments, or serve advertising content.
    </LegalP>

    <LegalH3>1.3 Scope and Applicable Law</LegalH3>
    <LegalP>
      This Privacy Policy is governed by and construed in accordance with the <strong>Kenya Data Protection Act, 2019 (Act No. 24 of 2019)</strong> ("KDPA") and its subsidiary regulations, including the Data Protection (General) Regulations, 2021. Where applicable, we further align our practices with internationally recognised data protection principles consistent with the General Data Protection Regulation (GDPR) framework, including the principles of lawfulness, fairness, transparency, purpose limitation, data minimisation, accuracy, storage limitation, integrity, and accountability.
    </LegalP>
    <LegalP>
      By accessing or using Gemio, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with any part of this Policy, you must discontinue use of our platform immediately.
    </LegalP>

    {/* SECTION 2 */}
    <LegalH2>2. Information We Collect</LegalH2>

    <LegalH3>2.1 Information Collected from Users (Diners)</LegalH3>
    <LegalP>When you register and use Gemio as a diner or visitor, we may collect the following categories of personal data:</LegalP>
    <LegalUL items={[
      "Full name — collected at account registration for identity purposes and personalisation.",
      "Email address — used for account authentication, booking confirmations, and service communications.",
      "Location data — collected only with your explicit consent to display geographically relevant restaurants and dining options near you.",
      "Reservation history — records of bookings made through the platform, including restaurant name, date, time, party size, and booking status.",
      "Account activity — interactions within the platform, such as restaurants viewed, favourites saved, searches conducted, and content engaged with.",
      "Device metadata — automatically collected by Firebase, including device type, operating system, app version, and anonymised device identifiers for app performance and security purposes.",
    ]} />

    <LegalH3>2.2 Information Collected from Restaurant Partners</LegalH3>
    <LegalP>When a restaurant or food establishment registers on Gemio as a business partner, we collect the following information:</LegalP>
    <LegalUL items={[
      "Business name and trading name.",
      "Business contact details, including email address and phone number.",
      "Physical business location and operational address.",
      "Menu images, PDFs, and associated content voluntarily uploaded by the restaurant to the platform.",
      "Reservation records associated with the restaurant's bookings.",
      "Dashboard activity, including login events, profile edits, and reservation management actions.",
    ]} />

    <LegalH3>2.3 Lawful Basis for Processing</LegalH3>
    <LegalP>In accordance with Section 30 of the Kenya Data Protection Act, 2019, we rely on the following lawful bases for processing personal data:</LegalP>
    <LegalUL items={[
      "Consent — for location data, marketing communications, and any non-essential data processing. You may withdraw consent at any time without prejudice to processing already carried out.",
      "Contractual Necessity — processing necessary to perform our agreement with you, including facilitating reservations, managing accounts, and providing the core platform services.",
      "Legitimate Interests — where processing is necessary for our legitimate operational interests such as fraud prevention, platform security, product improvement, and internal analytics, provided such interests do not override your fundamental rights.",
      "Legal Obligation — where processing is required to comply with a legal obligation under Kenyan law.",
    ]} />

    {/* SECTION 3 */}
    <LegalH2>3. How We Use Your Data</LegalH2>
    <LegalP>We process the personal data we collect for the following specific and lawful purposes:</LegalP>
    <LegalUL items={[
      "Facilitating and managing table reservations made through the platform.",
      "Creating and managing your Gemio user account and associated profile.",
      "Enabling restaurant partners to operate their business dashboards and manage reservation availability.",
      "Sending reservation confirmation, modification, and cancellation communications via email or in-app notification.",
      "Displaying geographically relevant restaurant listings based on your location (only where consent is given).",
      "Improving platform functionality, user experience, and service quality through internal analytics.",
      "Detecting and preventing fraudulent activities, unauthorised access, and abuse of the platform.",
      "Complying with applicable legal and regulatory obligations under Kenyan law.",
      "Communicating material updates to our terms, policies, or platform features.",
    ]} />
    <LegalP>
      We will not use your data for purposes incompatible with those stated above without first obtaining your explicit consent or establishing a new lawful basis.
    </LegalP>

    {/* SECTION 4 */}
    <LegalH2>4. Data Storage and Security</LegalH2>
    <LegalH3>4.1 Infrastructure</LegalH3>
    <LegalP>
      All data collected through Gemio is stored on <strong>Google Firebase</strong>, a cloud infrastructure platform provided by Google LLC. Firebase's servers may be located outside Kenya, including in the United States and the European Union. By using Gemio, you consent to the transfer and processing of your personal data on Firebase's secure cloud infrastructure. Such transfers are carried out subject to appropriate safeguards consistent with the KDPA and applicable data transfer frameworks.
    </LegalP>

    <LegalH3>4.2 Security Measures</LegalH3>
    <LegalP>We implement reasonable and appropriate technical and administrative security safeguards to protect your personal data, including:</LegalP>
    <LegalUL items={[
      "Encryption of data in transit using industry-standard TLS (Transport Layer Security) protocols.",
      "Firebase's built-in data encryption at rest for stored data.",
      "Role-based access control to restrict internal access to personal data on a need-to-know basis.",
      "Secure authentication mechanisms including email/password authentication and optional multi-factor authentication.",
      "Periodic review of security practices and platform configurations.",
    ]} />

    <LegalH3>4.3 Security Disclaimer</LegalH3>
    <LegalP>
      Whilst we take reasonable steps to protect your personal data, no method of transmission over the internet or method of electronic storage is one hundred percent (100%) secure. Gemio does not warrant or guarantee the absolute security of your data. In the event of a data breach that poses a risk to your rights and freedoms, we will notify you and the Office of the Data Protection Commissioner (ODPC) in accordance with the requirements of the KDPA.
    </LegalP>

    {/* SECTION 5 */}
    <LegalH2>5. Data Retention</LegalH2>
    <LegalP>We retain personal data only for as long as is necessary to fulfil the purposes for which it was collected, or as required by applicable law. Our specific retention periods are as follows:</LegalP>
    <LegalUL items={[
      "User account data (name, email, preferences): Retained for the duration of your active account, and deleted within thirty (30) days of account deletion unless a legal obligation requires longer retention.",
      "Reservation records: Retained for a period of twelve (12) months following the reservation date for service integrity, dispute resolution, and legal compliance purposes.",
      "Restaurant dashboard data: Retained for the duration of the restaurant's active listing, and deleted within thirty (30) days of account removal.",
      "Device metadata and analytics data: Retained in anonymised or aggregated form for up to twenty-four (24) months for product improvement purposes.",
      "Legal compliance data: Certain data may be retained beyond standard periods where required by applicable Kenyan law, including tax, consumer protection, or regulatory obligations.",
    ]} />

    {/* SECTION 6 */}
    <LegalH2>6. Your Rights Under the Kenya Data Protection Act, 2019</LegalH2>
    <LegalP>
      As a data subject under the KDPA, you are entitled to the following rights with respect to your personal data processed by Gemio:
    </LegalP>
    <LegalUL items={[
      "Right of Access (Section 26 KDPA) — You have the right to request confirmation of whether we hold your personal data and to receive a copy of such data.",
      "Right to Rectification (Section 26 KDPA) — You have the right to request correction of inaccurate, outdated, or incomplete personal data we hold about you.",
      "Right to Erasure (Section 26 KDPA) — You have the right to request deletion of your personal data where it is no longer necessary for the purpose for which it was collected, or where you withdraw consent.",
      "Right to Object (Section 26 KDPA) — You have the right to object to the processing of your personal data, including processing based on legitimate interests.",
      "Right to Withdraw Consent — Where processing is based on consent, you may withdraw that consent at any time without affecting the lawfulness of processing prior to withdrawal.",
      "Right to Data Portability — Where technically feasible, you have the right to receive your personal data in a structured, commonly used, and machine-readable format.",
      "Right to Lodge a Complaint — You have the right to lodge a complaint with the Office of the Data Protection Commissioner (ODPC) of Kenya if you believe your data rights have been violated.",
    ]} />

    <LegalH3>6.1 How to Submit a Data Rights Request</LegalH3>
    <LegalP>
      To exercise any of the above rights, please submit a written request to <strong>business.hiddengems@gmail.com</strong> with the subject line "Data Rights Request". We will respond to your request within <strong>twenty-one (21) days</strong> of receipt. We may request verification of your identity before processing your request to prevent unauthorised access to your data.
    </LegalP>

    {/* SECTION 7 */}
    <LegalH2>7. Account Deletion</LegalH2>
    <LegalH3>7.1 User Account Deletion</LegalH3>
    <LegalP>
      Users may delete their Gemio accounts at any time through the account settings within the application. Upon deletion, we will remove your personal data, including your name, email address, saved favourites, and account activity, within thirty (30) days of your request.
    </LegalP>

    <LegalH3>7.2 Restaurant Account Deletion</LegalH3>
    <LegalP>
      Restaurant partners may request deletion of their business account and associated dashboard data by contacting us at <strong>business.hiddengems@gmail.com</strong>. Upon confirmation, restaurant profile data, uploaded menus, and dashboard activity will be removed within thirty (30) days.
    </LegalP>

    <LegalH3>7.3 Data Retained Post-Deletion</LegalH3>
    <LegalP>
      Notwithstanding account deletion, Gemio may retain certain data where required by law or for legitimate legal compliance purposes. This may include reservation records required for dispute resolution, and anonymised or aggregated data that cannot be used to identify you individually.
    </LegalP>

    {/* SECTION 8 */}
    <LegalH2>8. QR Code and Digital Menu Feature</LegalH2>
    <LegalP>
      Gemio enables restaurant partners to upload menu images or PDF documents to their business profiles. Gemio generates a unique QR code linking to the restaurant's uploaded menu content. The following terms apply to this feature:
    </LegalP>
    <LegalUL items={[
      "Menu content is uploaded voluntarily and exclusively by the restaurant partner.",
      "Gemio does not verify, validate, or endorse the accuracy, completeness, pricing, or availability of any menu content uploaded by restaurants.",
      "Restaurants are solely responsible for ensuring that their uploaded menu content is accurate, current, and compliant with applicable laws.",
      "Users accessing menu content via QR code are advised to verify pricing and availability directly with the restaurant.",
      "Gemio accepts no liability for any discrepancy between the menu displayed on the platform and the actual menu served at the establishment.",
    ]} />

    {/* SECTION 9 */}
    <LegalH2>9. Online Reservation Toggle</LegalH2>
    <LegalP>
      Restaurant partners have the ability to enable or disable online reservations through their Gemio dashboard at any time. The following applies:
    </LegalP>
    <LegalUL items={[
      "When reservations are enabled, users may submit booking requests through the Gemio platform at https://gemio.app/",
      "Gemio acts solely as a reservation facilitation platform and does not guarantee confirmation of any reservation request.",
      "Restaurant partners are solely responsible for managing, confirming, modifying, or cancelling reservations.",
      "Gemio is not liable for service delivery failures, no-shows, overbookings, or any failure by a restaurant to honour a reservation.",
      "Users are advised to confirm reservations directly with the restaurant where possible.",
    ]} />

    {/* SECTION 10 */}
    <LegalH2>10. Age Restriction</LegalH2>
    <LegalP>
      Gemio's services are intended for individuals who are <strong>sixteen (16) years of age or older</strong>. We do not knowingly collect personal data from persons under the age of 16. If we become aware that an account has been registered by a person under the minimum age, we reserve the right to suspend or permanently remove such account and delete all associated personal data without prior notice.
    </LegalP>
    <LegalP>
      If you are a parent or guardian and believe your child under the age of 16 has created a Gemio account, please contact us immediately at <strong>business.hiddengems@gmail.com</strong>.
    </LegalP>

    {/* SECTION 11 */}
    <LegalH2>11. Third-Party Services</LegalH2>
    <LegalP>Gemio integrates with the following third-party service provider in the operation of our platform:</LegalP>
    <LegalUL items={[
      "Google Firebase (Google LLC) — serves as our backend infrastructure provider for database management, user authentication, cloud storage, and app performance monitoring. Firebase operates under Google's Privacy Policy and applicable data processing agreements.",
    ]} />
    <LegalP>
      Gemio does not integrate with, nor does it permit, any third-party advertising networks on the platform. We do not display advertisements to users. We do not integrate with any payment processors, as Gemio does not handle financial transactions. All payment-related activities between users and restaurants occur independently and outside the Gemio platform.
    </LegalP>
    <LegalP>
      Where your data is processed by Firebase on our behalf, such processing is subject to appropriate data processing safeguards. We are not responsible for the independent data practices of third-party providers and encourage you to review their respective privacy policies.
    </LegalP>

    {/* SECTION 12 */}
    <LegalH2>12. Changes to This Privacy Policy</LegalH2>
    <LegalP>
      We reserve the right to update or amend this Privacy Policy at any time to reflect changes in our practices, legal requirements, or platform functionality. Where changes are material, we will notify users via email to the registered address or through a prominent in-app notice at least <strong>fourteen (14) days</strong> prior to the change taking effect. Your continued use of Gemio following the effective date of any updated Privacy Policy constitutes your acceptance of the revised terms.
    </LegalP>

    {/* SECTION 13 */}
    <LegalH2>13. Contact Information</LegalH2>
    <LegalP>For all privacy-related enquiries, data rights requests, or concerns regarding this Privacy Policy, please contact:</LegalP>
    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 space-y-1">
      <p><strong>Data Controller:</strong> Gemio (Individual Operator, pre-incorporation)</p>
      <p><strong>Email:</strong> business.hiddengems@gmail.com</p>
      <p><strong>Phone:</strong> +254 768 691 034</p>
      <p><strong>Jurisdiction:</strong> Republic of Kenya</p>
      <p><strong>Response Time:</strong> Within 21 days of receipt</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   TERMS OF SERVICE CONTENT
───────────────────────────────────────────── */
const TermsOfServiceContent = () => (
  <div>
    <p className="text-xs text-gray-400 mb-1">Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: January 2025</p>
    <LegalNote>
      Please read these Terms of Service carefully. By accessing or using the Gemio platform, you enter into a legally binding agreement with Gemio. If you do not accept these Terms, you must not use the platform.
    </LegalNote>

    {/* SECTION 1 */}
    <LegalH2>1. Acceptance of Terms</LegalH2>
    <LegalP>
      By accessing, downloading, registering on, or otherwise using the Gemio mobile application or web platform (collectively, the "Platform"), you ("User", "you", or "your") confirm that you have read, understood, and irrevocably agree to be bound by these Terms of Service ("Terms"), our Privacy Policy, and any additional guidelines or policies incorporated herein by reference. These Terms constitute a legally binding agreement between you and Gemio.
    </LegalP>
    <LegalP>
      If you are accessing the Platform on behalf of a business entity (such as a restaurant or food establishment), you represent and warrant that you have the authority to bind such entity to these Terms, and all references to "you" shall include that entity.
    </LegalP>
    <LegalP>
      Gemio reserves the right to amend these Terms at any time. Continued use of the Platform following notification of any amendments constitutes acceptance of the revised Terms.
    </LegalP>

    {/* SECTION 2 */}
    <LegalH2>2. Description of Service</LegalH2>
    <LegalP>Gemio is a digital platform that provides the following services:</LegalP>
    <LegalUL items={[
      "Restaurant Discovery & Exploration — enabling users to discover, browse, and explore curated restaurant and dining establishments across Kenya, including details on cuisine type, location, operating hours, and user reviews.",
      "Digital Menu Access — enabling users to view restaurant menus through digitised content and QR code links generated from restaurant-uploaded materials.",
      "Online Table Reservations — enabling users to submit reservation requests to participating restaurants through the platform, accessible at https://gemio.app/.",
      "Restaurant Dashboard Tools — providing registered restaurant partners with tools to manage their business profiles, upload menu content, generate QR codes, manage reservations, and toggle online booking availability.",
    ]} />
    <LegalP>
      Gemio acts solely as an intermediary technology platform. We do not operate, own, or manage any food establishment. We do not prepare, serve, or deliver food. We are not a party to any agreement between a user and a restaurant arising from a reservation or visit.
    </LegalP>

    {/* SECTION 3 */}
    <LegalH2>3. Eligibility and Account Registration</LegalH2>
    <LegalP>
      To access certain features of the Platform, you must register for an account. By registering, you represent and warrant that: (i) you are at least <strong>sixteen (16) years of age</strong>; (ii) you have the legal capacity to enter into binding agreements under Kenyan law; (iii) all information you provide during registration is accurate, complete, and current; and (iv) you will maintain the accuracy of such information throughout your use of the Platform.
    </LegalP>
    <LegalP>
      You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at <strong>business.hiddengems@gmail.com</strong> if you suspect unauthorised access to your account. Gemio shall not be liable for any loss or damage arising from your failure to safeguard your account credentials.
    </LegalP>

    {/* SECTION 4 */}
    <LegalH2>4. User Responsibilities</LegalH2>
    <LegalP>As a user of the Gemio platform, you agree to the following obligations and restrictions:</LegalP>
    <LegalUL items={[
      "You will provide accurate, truthful, and complete information when registering an account and when making reservations.",
      "You will not make fraudulent, false, or speculative reservation bookings with no genuine intention to attend.",
      "You will not abuse the reservation system by making multiple conflicting bookings, systematically cancelling bookings, or otherwise misusing reservation functionality.",
      "You will not post false, defamatory, misleading, or malicious content, including reviews or ratings intended to harm a restaurant's reputation without factual basis.",
      "You will not use the Platform for any unlawful purpose or in violation of any applicable Kenyan law or regulation.",
      "You will not engage in any conduct that disrupts, damages, or impairs the integrity or functionality of the Platform.",
      "You will not attempt to gain unauthorised access to any portion of the Platform, other users' accounts, or backend systems.",
      "You will not use automated scraping, crawling, or data harvesting tools on the Platform without prior written authorisation from Gemio.",
    ]} />

    {/* SECTION 5 */}
    <LegalH2>5. Restaurant Partner Responsibilities</LegalH2>
    <LegalP>Registered restaurant partners agree to the following obligations:</LegalP>
    <LegalUL items={[
      "You will provide accurate, complete, and up-to-date business information on your Gemio profile, including business name, contact details, operating hours, and location.",
      "You are solely responsible for the accuracy, completeness, and currency of all menu content uploaded to the Platform, including pricing, item availability, and allergen information.",
      "You will promptly update your profile and menu content whenever material changes occur.",
      "You are solely responsible for managing, confirming, and honouring reservations received through the Platform.",
      "You will handle all customer service matters arising from reservations, visits, food quality, and dining experiences directly with users.",
      "You will enable or disable online reservations through your dashboard only in accordance with your actual operational capacity and availability.",
      "You will not upload false, misleading, or third-party owned content to your Gemio profile without proper authorisation.",
      "You represent and warrant that your business complies with all applicable Kenyan laws and regulations, including those governing food safety, licensing, and consumer protection.",
    ]} />

    {/* SECTION 6 */}
    <LegalH2>6. No Payment Handling</LegalH2>
    <LegalP>
      Gemio is expressly not a payment platform and does not process, facilitate, handle, hold, or transmit any financial transactions or payments of any kind. All financial transactions between users and restaurants — including table deposits, advance payments, in-restaurant billing, or any other monetary exchange — occur entirely outside the Gemio platform and are the sole responsibility of the restaurant and the user.
    </LegalP>
    <LegalP>
      Gemio shall have no liability whatsoever in connection with any payment dispute, overcharge, unauthorised charge, fraud, or financial loss arising from transactions conducted between users and restaurants.
    </LegalP>

    {/* SECTION 7 */}
    <LegalH2>7. Limitation of Liability</LegalH2>
    <LegalP>
      To the fullest extent permitted by applicable law, Gemio, its operator, affiliates, employees, agents, and licensors expressly disclaim all liability for any direct, indirect, incidental, consequential, special, exemplary, or punitive damages arising from or relating to your use of or inability to use the Platform, including but not limited to:
    </LegalP>
    <LegalUL items={[
      "The quality, safety, legality, or suitability of food, beverages, or services provided by any restaurant listed on the Platform.",
      "Any failed, unfulfilled, or disputed reservation between a user and a restaurant.",
      "No-shows by users or cancellations by restaurants.",
      "Inaccurate pricing, menu information, or promotional offers displayed by restaurants.",
      "Any loss or damage arising from reliance on restaurant-generated content, including menus, descriptions, or images.",
      "Platform downtime, technical errors, or service interruptions.",
      "Unauthorised access to your account resulting from your failure to secure your credentials.",
      "Any conduct or content of third parties on or through the Platform.",
    ]} />
    <LegalP>
      THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
    </LegalP>
    <LegalP>
      In no event shall Gemio's aggregate liability to any party exceed the amount of zero Kenya Shillings (KES 0), as Gemio does not charge users for access to the core platform services.
    </LegalP>

    {/* SECTION 8 */}
    <LegalH2>8. Indemnification</LegalH2>
    <LegalP>
      You agree to indemnify, defend, and hold harmless Gemio, its operator, officers, employees, agents, successors, and assigns from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or relating to:
    </LegalP>
    <LegalUL items={[
      "Your use of or access to the Platform in violation of these Terms.",
      "Your violation of any applicable law, regulation, or third-party right.",
      "Any false, inaccurate, or misleading information you provide through the Platform.",
      "Any fraudulent or abusive use of the reservation system.",
      "Any content you upload, post, or transmit through the Platform.",
      "Any dispute between you and a restaurant arising from a reservation or dining experience.",
    ]} />

    {/* SECTION 9 */}
    <LegalH2>9. Intellectual Property</LegalH2>
    <LegalH3>9.1 Gemio's Intellectual Property</LegalH3>
    <LegalP>
      All intellectual property rights in and to the Gemio Platform, including its source code, software architecture, design, user interface, branding, trademarks, trade names, logos, and original content created by Gemio, are and shall remain the exclusive property of Gemio and its operator. Nothing in these Terms grants you any right, title, or interest in Gemio's intellectual property.
    </LegalP>

    <LegalH3>9.2 Restaurant-Uploaded Content</LegalH3>
    <LegalP>
      Restaurant partners retain ownership of all content they upload to the Platform, including menu images, photos, business descriptions, and related materials ("Partner Content"). By uploading Partner Content, you grant Gemio a non-exclusive, royalty-free, worldwide licence to display, reproduce, and use such content solely for the purpose of operating and promoting the Platform and your listing thereon. You represent and warrant that you own or have obtained all necessary rights and permissions to upload such content.
    </LegalP>

    <LegalH3>9.3 User-Generated Content</LegalH3>
    <LegalP>
      By submitting reviews, ratings, or other content to the Platform, users grant Gemio a non-exclusive, royalty-free, perpetual licence to display and use such content on the Platform. Users represent that their content does not infringe any third-party rights and complies with these Terms.
    </LegalP>

    {/* SECTION 10 */}
    <LegalH2>10. Account Suspension</LegalH2>
    <LegalP>
      Gemio reserves the right, in its sole and absolute discretion, to suspend, restrict, or terminate any user or restaurant account — temporarily or permanently — without prior notice, for any of the following reasons:
    </LegalP>
    <LegalUL items={[
      "Breach of any provision of these Terms or our Privacy Policy.",
      "Fraudulent, abusive, or deceptive use of the Platform.",
      "Submission of false information or misrepresentation.",
      "Conduct that is harmful, offensive, or damaging to other users, restaurants, or the integrity of the Platform.",
      "Failure to comply with applicable Kenyan laws or regulations.",
      "Any conduct that Gemio, in its reasonable judgement, considers detrimental to the Platform or its users.",
    ]} />
    <LegalP>
      Suspension or termination of an account does not waive any rights or remedies available to Gemio at law.
    </LegalP>

    {/* SECTION 11 */}
    <LegalH2>11. Termination</LegalH2>
    <LegalH3>11.1 Termination by User</LegalH3>
    <LegalP>
      You may terminate your account at any time by deleting your account through the application settings or by submitting a written request to <strong>business.hiddengems@gmail.com</strong>. Upon termination, your personal data will be handled in accordance with our Privacy Policy.
    </LegalP>

    <LegalH3>11.2 Termination by Restaurant Partner</LegalH3>
    <LegalP>
      Restaurant partners may terminate their business account and remove their listing at any time by contacting <strong>business.hiddengems@gmail.com</strong>. Upon account termination, the restaurant's public profile will be removed from the Platform.
    </LegalP>

    <LegalH3>11.3 Termination by Gemio</LegalH3>
    <LegalP>
      Gemio may terminate or suspend access to the Platform at any time, with or without cause, and with or without notice. Upon termination by Gemio for cause, any outstanding obligations you owe to Gemio shall survive termination. Sections 7 (Limitation of Liability), 8 (Indemnification), 9 (Intellectual Property), 12 (Governing Law), and 13 (Dispute Resolution) shall survive any termination of these Terms.
    </LegalP>

    {/* SECTION 12 */}
    <LegalH2>12. Governing Law</LegalH2>
    <LegalP>
      These Terms of Service, and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims), shall be governed by and construed in accordance with the <strong>Laws of the Republic of Kenya</strong>, without regard to its conflict of law provisions.
    </LegalP>

    {/* SECTION 13 */}
    <LegalH2>13. Dispute Resolution</LegalH2>
    <LegalH3>13.1 Informal Resolution</LegalH3>
    <LegalP>
      In the event of any dispute, claim, or controversy arising out of or relating to these Terms or the use of the Platform, the parties agree to first attempt to resolve the matter informally. The disputing party must send a written notice to <strong>business.hiddengems@gmail.com</strong> describing the nature of the dispute and the relief sought. The parties shall endeavour to resolve the matter within <strong>thirty (30) days</strong> of receipt of such notice.
    </LegalP>

    <LegalH3>13.2 Formal Proceedings</LegalH3>
    <LegalP>
      If the parties are unable to resolve the dispute informally within thirty (30) days, either party may refer the matter to the competent courts of the Republic of Kenya, which shall have exclusive jurisdiction over all disputes arising under these Terms. You agree to submit to the personal jurisdiction of the Kenyan courts for this purpose.
    </LegalP>

    {/* SECTION 14 */}
    <LegalH2>14. Miscellaneous</LegalH2>
    <LegalUL items={[
      "Severability — If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.",
      "Entire Agreement — These Terms, together with the Privacy Policy, constitute the entire agreement between you and Gemio regarding the Platform and supersede all prior agreements.",
      "No Waiver — Failure by Gemio to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.",
      "Assignment — You may not assign your rights or obligations under these Terms without Gemio's prior written consent. Gemio may assign its rights and obligations without restriction.",
    ]} />

    <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 mt-6 space-y-1">
      <p><strong>Contact for Legal Queries:</strong> business.hiddengems@gmail.com</p>
      <p><strong>Phone:</strong> +254 768 691 034</p>
      <p><strong>Jurisdiction:</strong> Republic of Kenya</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   COOKIE POLICY CONTENT
───────────────────────────────────────────── */
const CookiePolicyContent = () => (
  <div>
    <p className="text-xs text-gray-400 mb-1">Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: January 2025</p>
    <LegalNote>
      This Cookie Policy explains how Gemio uses cookies and similar technologies when you use our web and mobile platform.
    </LegalNote>

    <LegalH2>1. What Are Cookies?</LegalH2>
    <LegalP>
      Cookies are small text files placed on your device (computer, smartphone, or tablet) when you visit a website or use a digital application. They are widely used to make websites and apps function more efficiently, remember user preferences, and provide information to platform operators about usage patterns.
    </LegalP>

    <LegalH2>2. Cookies Used by Gemio</LegalH2>
    <LegalH3>Essential / Functional Cookies</LegalH3>
    <LegalP>
      These cookies are strictly necessary for the Platform to function. They enable core features such as user authentication, session management, and secure login. You cannot opt out of essential cookies as they are required for the Platform to operate.
    </LegalP>

    <LegalH3>Preference Cookies</LegalH3>
    <LegalP>
      These cookies remember your settings and preferences, such as your preferred location, language settings, and display preferences, to provide you with a personalised experience.
    </LegalP>

    <LegalH3>Analytics Cookies</LegalH3>
    <LegalP>
      We use Firebase Analytics, provided by Google LLC, to collect anonymised data about how users interact with the Platform. This helps us understand usage patterns, identify areas for improvement, and optimise the user experience. Analytics data is collected in aggregated, anonymised form and is not used to identify individual users.
    </LegalP>

    <LegalH3>Marketing Cookies</LegalH3>
    <LegalP>
      Gemio does not currently use marketing or advertising cookies, as we do not operate an advertising programme on our Platform.
    </LegalP>

    <LegalH2>3. Third-Party Cookies</LegalH2>
    <LegalP>
      Certain cookies on the Platform may be placed by Google Firebase as part of our backend infrastructure. These cookies are governed by Google's Privacy Policy, available at google.com/privacy. We do not permit third-party advertising networks to place cookies on our Platform.
    </LegalP>

    <LegalH2>4. Managing Your Cookie Preferences</LegalH2>
    <LegalP>
      You may control and manage cookies through your browser or device settings. Most browsers allow you to block, delete, or restrict cookies. Please note that disabling non-essential cookies may affect the functionality and personalisation features of the Platform. Instructions for managing cookies in common browsers are available on the browser provider's support pages.
    </LegalP>

    <LegalH2>5. Changes to This Cookie Policy</LegalH2>
    <LegalP>
      We may update this Cookie Policy from time to time. Any material changes will be communicated via in-app notification or email. Your continued use of the Platform following any update constitutes acceptance of the revised Cookie Policy.
    </LegalP>

    <LegalH2>6. Contact</LegalH2>
    <LegalP>
      For questions regarding our use of cookies, please contact us at <strong>business.hiddengems@gmail.com</strong>.
    </LegalP>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function InfoPage() {
  const [activeSection, setActiveSection] = useState('About');

  const sections = [
    { id: 'about', label: 'About', icon: Info },
    { id: 'help', label: 'Help Center', icon: HelpCircle },
    { id: 'terms', label: 'Terms', icon: FileText },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'cookies', label: 'Cookies', icon: Cookie },
  ];

  return (
    <>
      <Helmet>
        <title>About & Policies – Gemio</title>
        <meta name="description" content="Learn more about Gemio and our mission to simplify restaurant reservations while helping diners discover exceptional dining experiences across Kenya." />
      </Helmet>
      <div className="min-h-screen bg-white">

        {/* Sticky Navigation */}
        <nav className="sticky top-0 bg-white border-b border-gray-200 z-19 shadow-sm">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center justify-between lg:justify-start lg:gap-2 w-full">
                {sections.map(s => (
                  <NavLink key={s.id} href={`#${s.id}`} icon={s.icon} setActiveSection={setActiveSection} activeSection={activeSection}>
                    {s.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-4xl mx-auto px-4 py-8">

          {/* ── ABOUT ── */}
          <section id="about" className="mb-16 scroll-mt-20">
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">About Gemio</h1>
              <p className="text-xl text-amber-600 font-medium leading-snug">
                Discover extraordinary dining experiences hiding in plain sight — restaurants, menus, and reservations, all in one place.
              </p>
            </div>

            <div className="bg-amber-50 rounded-2xl p-5 mb-8">
              <p className="text-gray-700 leading-relaxed text-base">
                Gemio is Kenya's restaurant discovery platform built for food lovers who want more than just a list of places to eat. We help you explore authentic local flavours, hidden culinary gems, and memorable dining experiences — from street-side cafés to fine dining in Nairobi and beyond. Browse menus before you arrive, make a reservation in seconds, and discover the stories behind the kitchens you love.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: MapPin, value: '100+', label: 'Restaurants' },
                { icon: Users, value: '1.5K+', label: 'Food Lovers' },
                { icon: Star, value: '2.0K+', label: 'Reviews' },
                { icon: Heart, value: '4.1K+', label: 'Favourites' },
              ].map((stat, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                  <stat.icon className="mx-auto text-amber-500 mb-2" size={24} />
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* What We Offer */}
            <h3 className="text-xl font-semibold text-gray-900 mb-4">What Gemio Offers</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { icon: Search, title: 'Curated Discovery', desc: 'Browse hand-picked restaurants vetted by local food enthusiasts and neighbourhood insiders.' },
                { icon: BookOpen, title: 'Digital Menus', desc: 'Explore full restaurant menus before you arrive — no surprises, just anticipation.' },
                { icon: ExternalLink, title: 'Easy Reservations', desc: 'Book your table directly through Gemio at https://gemio.app/ with instant confirmation.' },
                { icon: QrCode, title: 'QR Menu Access', desc: 'Restaurants generate scannable QR codes linking diners to their latest digital menus.' },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-4 flex gap-3">
                  <item.icon size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm">{item.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reservation CTA */}
            <div className="bg-gray-900 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-white font-bold text-lg mb-1">Ready to book your next table?</h3>
                <p className="text-gray-400 text-sm">Browse available restaurants and reserve your spot in seconds.</p>
              </div>
              <a
                href="https://gemio.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-3 rounded-xl transition-colors text-sm"
              >
                <ToggleRight size={18} />
                Make a Reservation
              </a>
            </div>
          </section>

          {/* ── HELP CENTER ── */}
          <section id="help" className="mb-16 scroll-mt-20">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-1">Help Center</h2>
              <p className="text-gray-500 text-sm">Everything you need to get the most out of Gemio.</p>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: Search, label: 'Search Tips' },
                { icon: Heart, label: 'Favourites' },
                { icon: MapPin, label: 'Add Venue' },
              ].map((action, i) => (
                <button key={i} className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-colors">
                  <action.icon size={22} className="text-amber-600" />
                  <span className="text-xs font-medium text-gray-700">{action.label}</span>
                </button>
              ))}
            </div>

            {/* FAQ – Visitors */}
            <h3 className="text-base font-semibold text-gray-900 mb-3">For Diners & Visitors</h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <FAQItem question="How do I search for restaurants?" answer="Use the search bar at the top of the app to filter by cuisine type (e.g. Ethiopian, Italian, Swahili Coast), location or neighbourhood, budget range, and dining experience (romantic, family-friendly, outdoor seating). Results update in real time as you type." />
              <FAQItem question="How do I view a restaurant's menu?" answer="Open any restaurant listing and tap 'View Menu' to browse their digital menu. If the restaurant has enabled QR menus, you can also scan their QR code in person to access the latest version directly." />
              <FAQItem question="How do I make a reservation?" answer="Visit https://gemio.app/ or tap 'Book a Table' on any participating restaurant's listing. Select your preferred date, time, and party size. You'll receive instant confirmation by email and in-app notification." />
              <FAQItem question="How do I save a restaurant to my favourites?" answer="Tap the heart icon on any restaurant card to save it to your favourites list. Access all saved restaurants from your profile under 'My Favourites'. You can also create custom lists like 'Date Night' or 'Weekend Brunch'." />
              <FAQItem question="How do reviews and ratings work?" answer="After dining at a restaurant you can leave a star rating (1–5) and a written review. Reviews help other food lovers make informed decisions. Please keep all reviews honest, constructive, and respectful." />
              <FAQItem question="What is the minimum age to use Gemio?" answer="You must be at least 16 years old to create a Gemio account and use our services." />
            </div>

            {/* FAQ – Restaurant Owners */}
            <h3 className="text-base font-semibold text-gray-900 mb-3">For Restaurant Owners</h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <FAQItem question="How do I list my restaurant on Gemio?" answer="Access your creator dashboard and follow the step-by-step onboarding process. You can add your business details, operating hours, photos, menu content, and special offers. Your listing typically goes live within minutes of completing setup." />
              <FAQItem question="How do I upload my menu?" answer="From your dashboard, upload menu images or a PDF document. Gemio will automatically generate a QR code that links diners to your uploaded menu. You can update your menu at any time and the QR code will reflect the latest version." />
              <FAQItem question="How do I manage online reservations?" answer="You can enable or disable online reservations from your dashboard at any time using the reservation toggle. When enabled, users can book through https://gemio.app/. You are responsible for confirming, managing, and honouring all bookings received." />
              <FAQItem question="Can I edit my listing after publishing?" answer="Yes. Access your dashboard at any time to update business details, photos, menu items, opening hours, or special announcements. Changes go live immediately." />
              <FAQItem question="How can I respond to customer reviews?" answer="Restaurant owners can reply to any review from their dashboard. We encourage thoughtful, professional responses to both positive and negative feedback — it builds trust with potential diners." />
            </div>

            {/* Contact */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Need More Help?</h3>
              <p className="text-gray-600 text-sm mb-4">Our team is available to assist with any questions or issues you're experiencing. Visit <span className='text-amber-600 hover:underline'><a href="/support">Our support</a></span></p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="mailto:business.hiddengems@gmail.com" className="inline-flex items-center justify-center gap-2 bg-amber-500 text-white px-5 py-3 rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors">
                  <Mail size={16} />
                  business.hiddengems@gmail.com
                </a>
                <a href="tel:+254768691034" className="inline-flex items-center justify-center gap-2 border border-gray-300 px-5 py-3 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                  <Phone size={16} />
                  +254 768 691 034
                </a>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Clock size={14} />
                <span>We typically respond within 24 hours.</span>
              </div>
            </div>
          </section>

          {/* ── TERMS ── */}
          <section id="terms" className="mb-6 scroll-mt-20">
            <CollapsibleSection title="Terms of Service" icon={FileText} defaultOpen={false}>
              <TermsOfServiceContent />
            </CollapsibleSection>
          </section>

          {/* ── PRIVACY ── */}
          <section id="privacy" className="mb-6 scroll-mt-20">
            <CollapsibleSection title="Privacy Policy" icon={Shield} defaultOpen={false}>
              <PrivacyPolicyContent />
            </CollapsibleSection>
          </section>

          {/* ── COOKIES ── */}
          <section id="cookies" className="mb-16 scroll-mt-20">
            <CollapsibleSection title="Cookie Policy" icon={Cookie} defaultOpen={false}>
              <CookiePolicyContent />
            </CollapsibleSection>
          </section>

          {/* Footer note */}
          <div className="text-center text-xs text-gray-400 pb-8">
            <p>© {new Date().getFullYear()} Gemio. All rights reserved. Operating in the Republic of Kenya.</p>
            <p className="mt-1">These documents were prepared for Gemio and are subject to review by a licensed Kenyan legal practitioner.</p>
          </div>

        </main>
      </div>
    </>
  );
}